<template>
  <div class="page-container">
    <div class="page-header">
      <div>
        <h2 class="page-title">数据库日志</h2>
      </div>
    </div>

    <el-card class="search-card" shadow="never">
      <div class="search-bar">
        <el-form :inline="true" :model="query" size="small" class="search-form">
          <el-form-item label="时间范围">
            <el-date-picker
              v-model="query.timeRange"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              value-format="yyyy-MM-dd HH:mm:ss"
              :default-time="['00:00:00', '23:59:59']"
              :picker-options="pickerOptions"
              style="width: 360px"
            />
          </el-form-item>
          <el-form-item label="操作人">
            <el-input v-model="query.admin_name" placeholder="姓名" clearable style="width: 130px" @keyup.enter.native="handleSearch" />
          </el-form-item>
          <el-form-item label="语句关键词">
            <el-input v-model="query.sql" placeholder="如表名、update" clearable style="width: 170px" @keyup.enter.native="handleSearch" />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="action-bar">
          <span class="range-tip">实际查询范围：{{ effectiveRange }}</span>
          <span v-if="keywordTip" class="range-tip keyword-tip">{{ keywordTip }}</span>
        </div>
      </div>
    </el-card>

    <el-card class="table-card" shadow="never">
      <el-table v-loading="loading" :data="list" border size="small" height="100%">
        <el-table-column prop="created_at" label="执行时间" width="150"/>
        <el-table-column label="操作人" width="110">
          <template slot-scope="scope">
            {{ scope.row.admin_name || '系统' }}
          </template>
        </el-table-column>
        <el-table-column label="客户端 IP" width="130">
          <template slot-scope="scope">
            {{ scope.row.ip || '—' }}
          </template>
        </el-table-column>
        <el-table-column label="类型" width="90" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" :type="sqlTypeTag(scope.row.sql)" effect="plain">{{ sqlType(scope.row.sql) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="语句" min-width="400" show-overflow-tooltip>
          <template slot-scope="scope">
            <span class="cell-sql">{{ scope.row.sql }}</span>
          </template>
        </el-table-column>
        <el-table-column label="耗时" width="90" align="right">
          <template slot-scope="scope">
            <span :class="scope.row.time > 1000 ? 'cell-slow' : ''">{{ scope.row.time }} ms</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="80" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="handleDetail(scope.row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrap">
        <el-pagination
          :current-page="page"
          :page-size="pageSize"
          :total="total"
          layout="total, prev, pager, next"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <el-dialog title="语句详情" :visible.sync="detailVisible" width="760px" append-to-body>
      <el-descriptions :column="2" size="small" border v-if="currentRow">
        <el-descriptions-item label="执行时间">{{ currentRow.created_at }}</el-descriptions-item>
        <el-descriptions-item label="耗时">{{ currentRow.time }} ms</el-descriptions-item>
        <el-descriptions-item label="操作人">{{ currentRow.admin_name || '系统' }}</el-descriptions-item>
        <el-descriptions-item label="账号编号">{{ currentRow.admin_id }}</el-descriptions-item>
        <el-descriptions-item label="客户端 IP">{{ currentRow.ip || '—' }}</el-descriptions-item>
        <el-descriptions-item label="类型">{{ sqlType(currentRow.sql) }}</el-descriptions-item>
        <el-descriptions-item label="连接" :span="2">{{ currentRow.connection || '—' }}<span v-if="currentRow.database"> · {{ currentRow.database }}</span></el-descriptions-item>
        <el-descriptions-item label="语句" :span="2">
          <pre class="sql-text">{{ currentRow.sql }}</pre>
        </el-descriptions-item>
        <el-descriptions-item label="参数" :span="2">
          <pre class="sql-text sql-text-params">{{ formatBindings(currentRow.bindings) }}</pre>
        </el-descriptions-item>
      </el-descriptions>
      <div slot="footer">
        <el-button size="small" icon="el-icon-document-copy" @click="copySql">复制语句</el-button>
        <el-button size="small" @click="detailVisible = false">关 闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getSqlLogList } from '@/api/log'

// 语句类型标签：与操作类型区分开，这里按语句首词判定（只用于界面展示）
const TYPE_TAG = {
  SELECT: 'info',
  INSERT: 'success',
  UPDATE: 'warning',
  DELETE: 'danger',
  REPLACE: 'warning',
  ALTER: 'primary',
  CREATE: 'primary',
  DROP: 'danger',
  TRUNCATE: 'danger'
}

export default {
  name: 'SqlLog',
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      query: { timeRange: [], admin_name: '', sql: '' },
      page: 1,
      pageSize: 20,
      // 实际生效的时间范围（后端回显，未选时间时为默认的最近 24 小时）
      rangeStart: '',
      rangeEnd: '',
      // 语句关键词的匹配范围（后端回显：语句密文无法在库内检索，只在范围内解密过滤）
      keywordScanned: 0,
      keywordTruncated: false,
      detailVisible: false,
      currentRow: null,
      pickerOptions: {
        shortcuts: [
          {
            text: '最近 1 小时',
            onClick: (picker) => {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: '最近 24 小时',
            onClick: (picker) => {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 24 * 3600 * 1000)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: '今天',
            onClick: (picker) => {
              const start = new Date()
              start.setHours(0, 0, 0, 0)
              picker.$emit('pick', [start, new Date()])
            }
          },
          {
            text: '最近 7 天',
            onClick: (picker) => {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 7 * 24 * 3600 * 1000)
              picker.$emit('pick', [start, end])
            }
          }
        ]
      }
    }
  },
  computed: {
    effectiveRange() {
      if (!this.rangeStart && !this.rangeEnd) {
        return '—'
      }
      return this.rangeStart + ' ~ ' + this.rangeEnd
    },
    // 关键词检索的作用范围提示：未填关键词不提示；达扫描上限时提示缩小时间范围
    keywordTip() {
      const keyword = (this.query.sql || '').trim()
      if (!keyword) {
        return ''
      }
      if (this.keywordTruncated) {
        return '关键词只在最近 ' + this.keywordScanned + ' 条内匹配，范围已达上限，请缩小时间范围后再搜'
      }
      return '关键词只在本次范围内的 ' + this.keywordScanned + ' 条内匹配'
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    // 列表（后端做筛选与分页；语句关键词是正则匹配，必须带时间范围，不传默认最近 24 小时）
    loadData() {
      this.loading = true
      const params = { page: this.page, page_size: this.pageSize }
      const range = this.query.timeRange || []
      if (range.length === 2) {
        params.start_date = range[0]
        params.end_date = range[1]
      }
      const adminName = (this.query.admin_name || '').trim()
      if (adminName) params.admin_name = adminName
      const sql = (this.query.sql || '').trim()
      if (sql) params.sql = sql
      getSqlLogList(params).then(res => {
        if (res.code !== 200) {
          this.list = []
          this.total = 0
          return
        }
        const data = res.data || {}
        this.list = data.list || []
        this.total = data.total || 0
        this.rangeStart = data.start_date || ''
        this.rangeEnd = data.end_date || ''
        this.keywordScanned = data.keyword_scanned || 0
        this.keywordTruncated = data.keyword_truncated === true
      }).finally(() => {
        this.loading = false
      })
    },
    // 语句首词当类型（去掉前导空白与括号，取不到算「其他」）
    sqlType(sql) {
      const text = String(sql || '').replace(/^[\s(]+/, '')
      const matched = text.match(/^[a-zA-Z]+/)
      return matched ? matched[0].toUpperCase() : '其他'
    },
    sqlTypeTag(sql) {
      return TYPE_TAG[this.sqlType(sql)] || 'info'
    },
    formatBindings(bindings) {
      if (!bindings || bindings.length === 0) {
        return '无参数'
      }
      if (typeof bindings === 'string') {
        return bindings
      }
      try {
        return JSON.stringify(bindings, null, 2)
      } catch (e) {
        return String(bindings)
      }
    },
    copySql() {
      if (!this.currentRow || !this.currentRow.sql) {
        return
      }
      const input = document.createElement('textarea')
      input.value = this.currentRow.sql
      input.style.position = 'fixed'
      input.style.opacity = '0'
      document.body.appendChild(input)
      input.select()
      try {
        document.execCommand('copy')
        this.$message && this.$message.success('语句已复制')
      } catch (e) {
        this.$message && this.$message.warning('复制失败，请手动选择')
      }
      document.body.removeChild(input)
    },
    handleSearch() {
      this.page = 1
      this.loadData()
    },
    handlePageChange(val) {
      this.page = val
      this.loadData()
    },
    resetSearch() {
      this.query = { timeRange: [], admin_name: '', sql: '' }
      this.page = 1
      this.loadData()
    },
    handleDetail(row) {
      this.currentRow = row
      this.detailVisible = true
    }
  }
}
</script>

<style scoped>
.cell-sql {
  font-family: Menlo, Consolas, monospace;
  font-size: 12px;
}
.cell-slow {
  color: #e6a23c;
}
.range-tip {
  font-size: 12px;
  color: #909399;
}
.keyword-tip {
  margin-left: 14px;
  color: #e6a23c;
}
.sql-text {
  margin: 0;
  max-height: 240px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 12px;
  line-height: 1.6;
  background: #f7f8fa;
  padding: 8px 10px;
  border-radius: 4px;
}
.sql-text-params {
  color: #606266;
}
</style>
